/**
 * The Filters popover's model, shared by every report page.
 *
 * Ported from sellokart-cloud's `features/reports/reportFilters.ts`. Every
 * report page in this portal already fetches its full result set for the
 * selected date range (only Orders is server-paginated), so these rows are
 * applied with `applyClientFilters` on the already-fetched data rather than
 * sent to the API — Orders is the one exception, which serialises them into
 * the `filters`/`matchAny` query params the same way sellokart's
 * server-paginated `sales-history` does.
 */

export const FILTER_CONDITIONS = ['contains', 'equals', 'starts with', 'ends with'] as const;

export type FilterCondition = (typeof FILTER_CONDITIONS)[number];

export interface ReportFilterRow {
  field: string;
  condition: FilterCondition;
  value: string;
}

export function newFilterRow(field: string): ReportFilterRow {
  return { field, condition: 'contains', value: '' };
}

/** Rows the user actually filled in — an empty value is no constraint. */
export function activeFilters(rows: readonly ReportFilterRow[]): ReportFilterRow[] {
  return rows.filter((row) => row.value.trim() !== '');
}

/**
 * Serialise for the `filters` query param. Returns undefined when nothing
 * is set, so the param is omitted rather than sent as an empty array.
 */
export function serialiseFilters(rows: readonly ReportFilterRow[]): string | undefined {
  const active = activeFilters(rows);
  return active.length === 0 ? undefined : JSON.stringify(active);
}

export function parseFilterRows(serialised: string | null | undefined): ReportFilterRow[] {
  if (!serialised) return [];
  try {
    const parsed: unknown = JSON.parse(serialised);
    return Array.isArray(parsed) ? (parsed as ReportFilterRow[]) : [];
  } catch {
    // A hand-mangled URL must not break the page — treat it as no filter.
    return [];
  }
}
